import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /**
   * C16-PREP T2-C R5: ChunkLoadError handling.
   * `true` cuando el error capturado es un ChunkLoadError (chunk lazy
   * inexistente post-deploy). En ese caso renderizamos UI específica
   * "Nueva versión disponible" + auto-reload tras 3s.
   */
  isChunkLoadError: boolean;
}

/**
 * C16-PREP T2-C R5: Detecta ChunkLoadError post-deploy.
 *
 * Cuando un usuario tiene la app abierta en buildA y deployamos buildB, los
 * chunks lazy con hash buildA dejan de existir en `/assets/`. Al navegar a
 * una ruta lazy, Vite/webpack rejecta el `import()` con `ChunkLoadError`.
 *
 * Detección por (a) `error.name === 'ChunkLoadError'` (Webpack/Vite emit), o
 * (b) message regex `Loading chunk \d+ failed` / `Failed to fetch dynamically
 * imported module` (Vite emit). Cualquiera de los dos cae en el fallback de
 * recargar.
 */
function isChunkLoadErrorLike(error: Error): boolean {
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message || '';
  if (/Loading chunk \d+ failed/i.test(msg)) return true;
  if (/Failed to fetch dynamically imported module/i.test(msg)) return true;
  if (/error loading dynamically imported module/i.test(msg)) return true;
  return false;
}

/**
 * ErrorBoundary - Componente de clase que captura errores en el arbol de componentes
 *
 * Uso:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * O con fallback personalizado:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  private autoReloadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Actualiza el estado para mostrar la UI de error
    return {
      hasError: true,
      error,
      isChunkLoadError: isChunkLoadErrorLike(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log del error para debugging/monitoreo
    this.logError(error, errorInfo);

    const isChunkLoad = isChunkLoadErrorLike(error);

    this.setState({
      error,
      errorInfo,
      isChunkLoadError: isChunkLoad,
    });

    // C16-PREP T2-C R5: ChunkLoadError → auto-reload tras 3s para recoger
    // la nueva versión deployada. El usuario también ve botón "Recargar"
    // explícito por si quiere reaccionar antes.
    if (isChunkLoad && typeof window !== 'undefined') {
      this.autoReloadTimer = setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }

  componentWillUnmount(): void {
    if (this.autoReloadTimer) {
      clearTimeout(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }
  }

  /**
   * Registra el error en consola y puede extenderse para
   * enviar a servicios de monitoreo (Sentry, LogRocket, etc.)
   */
  private logError(error: Error, errorInfo: ErrorInfo): void {
    console.group('ErrorBoundary: Error capturado');
    console.error('Error:', error);
    console.error('Mensaje:', error.message);
    console.error('Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    // TODO: Aqui puedes agregar integracion con servicios de monitoreo
    // Ejemplo con Sentry:
    // Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  /**
   * Reinicia el estado del ErrorBoundary para intentar renderizar de nuevo
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false,
    });
  };

  /**
   * Recarga la pagina completamente
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, isChunkLoadError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // C16-PREP T2-C R5: ChunkLoadError post-deploy → UI específica
      // "Nueva versión disponible" con auto-reload 3s + botón explícito.
      // No usa el fallback custom (queremos UX consistente para upgrade).
      if (isChunkLoadError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6">
                <svg
                  className="w-8 h-8 text-blue-500 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Nueva versión disponible
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Hemos actualizado la app. Recargaremos automáticamente en
                unos segundos para que continues con la última versión.
              </p>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Recargar ahora
              </button>
              <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                Auto-recargando…
              </p>
            </div>
          </div>
        );
      }

      // Si se proporciona un fallback personalizado, usarlo
      if (fallback) {
        return fallback;
      }

      // UI de error por defecto
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            {/* Icono de error */}
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Titulo */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Algo salió mal
            </h1>

            {/* Descripcion */}
            <p className="text-gray-600 mb-6">
              Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado
              y estamos trabajando para solucionarlo.
            </p>

            {/* Detalles del error (solo en desarrollo) */}
            {import.meta.env.DEV && error && (
              <div className="mb-6 text-left">
                <details className="bg-gray-100 rounded-lg p-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    Ver detalles del error
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div className="text-xs">
                      <span className="font-semibold text-red-600">Error:</span>
                      <pre className="mt-1 p-2 bg-red-50 rounded text-red-800 overflow-x-auto">
                        {error.message}
                      </pre>
                    </div>
                    {error.stack && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-600">Stack:</span>
                        <pre className="mt-1 p-2 bg-gray-200 rounded text-gray-700 overflow-x-auto max-h-32 overflow-y-auto">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    {errorInfo?.componentStack && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-600">Component Stack:</span>
                        <pre className="mt-1 p-2 bg-gray-200 rounded text-gray-700 overflow-x-auto max-h-32 overflow-y-auto">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* Botones de accion */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Reintentar
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Recargar página
              </button>
            </div>

            {/* Link de soporte */}
            <p className="mt-6 text-sm text-gray-500">
              Si el problema persiste,{' '}
              <a
                href="mailto:soporte@solidcycle.com"
                className="text-primary-500 hover:text-primary-600 underline"
              >
                contacta a soporte
              </a>
            </p>

            {/* Version para debug */}
            <p className="mt-2 text-xs text-gray-400">
              v2026.01.13-FIX3
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
